import { FormHandles, Scope } from '@unform/core';
import { DeleteWarehouseMemberActions as DeleteActions } from 'store/ducks/warehouseMembers';
import { Form } from '@unform/web';
import {
  Alert,
  HiddenInput,
  Input,
  Modal,
  Select,
  ToggleInput,
} from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useValidation, useWarehouses } from 'hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchWarehouseMemberActions as FetchActions,
  UpdateWarehouseMemberActions as UpdateActions,
} from 'store/ducks/warehouseMembers';
import { ListWarehousesActions } from 'store/ducks/warehouses';
import { UpdateMemberValidator } from 'validators/WarehouseMembers';
import * as S from './styles';

interface Props {
  memberId: string | number;
  onUpdate?: () => void;
}

export const MemberUpdateForm: React.FC<Props> = ({ memberId, onUpdate }) => {
  const dispatch: AppDispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();
  const { warehouseOptions, loadingWarehouses, fetchWarehouses } =
    useWarehouses();

  const { data: member } = useSelector(
    (state: RootState) => state.fetchWarehouseMember
  );

  const { loading: updatingMember, validationErrors } = useSelector(
    (state: RootState) => state.updateWarehouseMember
  );
  const { loading: deletingMember } = useSelector(
    (state: RootState) => state.deleteWarehouseMember
  );

  const fetchmember = useCallback((): void => {
    dispatch(FetchActions.request(memberId));
  }, [dispatch, memberId]);

  const onMemberLoad = useCallback((): void => {
    if (!member) return;

    const { warehouse, user, userId } = member;

    const warehouseOption = warehouseOptions.find(
      (o) => o.value === warehouse.id
    );

    if (warehouseOption) {
      formRef.current?.setFieldValue('warehouseId', warehouseOption);
    }

    formRef.current?.setFieldValue('userId', userId);
    formRef.current?.setFieldValue('user.name', user.name);
    formRef.current?.setFieldValue('user.email', user.email);
    formRef.current?.setFieldValue(
      'user.blockedAt',
      user.blockedAt !== null ? '1' : '0'
    );
  }, [member, warehouseOptions]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateMemberValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(memberId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, memberId, onSuccess]
  );

  const onDelete = useCallback(() => {
    setModalOpen(false);
    dispatch(DeleteActions.request(memberId, onSuccess));
  }, [dispatch, handleFormErrors, memberId]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    fetchmember();
  }, [fetchmember]);

  useEffect(() => {
    onMemberLoad();
  }, [onMemberLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
      dispatch(ListWarehousesActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <HiddenInput name="userId" />
        <Select
          name="warehouseId"
          label="Armazém"
          options={warehouseOptions}
          isLoading={loadingWarehouses}
        />
        <Scope path="user">
          <S.FormRow>
            <Input name="name" label="Nome completo" />
            <Input name="email" label="Email" />
            <Input name="password" label="Senha" type="password" />
            <ToggleInput name="blockedAt" label="Bloqueado" />
          </S.FormRow>
        </Scope>
        <Modal isOpen={modalOpen} onClickOutside={() => setModalOpen(false)}>
          <Alert
            title={'Excluir Usuário'}
            text={'Deseja realmente excluir este usuário?'}
            close={() => setModalOpen(false)}
            action={onDelete}
            labelAction={'Sim'}
          />
        </Modal>
        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/armazens/usuarios">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit" disabled={updatingMember}>
            {updatingMember ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
          <S.Button
            type="button"
            mood="danger"
            onClick={() => setModalOpen(true)}
            disabled={deletingMember}
          >
            {deletingMember ? <S.ActivityIndicator /> : 'Excluir'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
